import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHideAmounts } from '@/hooks/useHideAmounts';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BusinessDashboard {
  period: { type: string; start_date: string; end_date: string };
  kpis: {
    total_revenue?: number;
    revenue_change_pct?: number;
    total_outstanding?: number;
    total_overdue?: number;
    revenue_per_employee?: number;
    active_employees: number;
    billable_hours: number;
    total_hours: number;
    active_clients: number;
    active_matters: number;
    average_rate?: number;
    entries_count: number;
  };
  comparison: {
    current: { revenue?: number; hours: number; entries: number; employees: number };
    previous: { revenue?: number; hours: number; entries: number; employees: number };
    changes: { revenue_pct?: number; hours_pct: number; entries_pct: number };
  };
  monthly_revenue: Array<{
    month: string;
    month_short: string;
    revenue?: number;
    hours: number;
    total_hours: number;
    entries: number;
  }>;
  revenue_by_day: Array<{
    day: string;
    day_short: string;
    revenue?: number;
    hours: number;
    entries: number;
  }>;
  team_performance: Array<{
    rank: number;
    id: number;
    name: string;
    revenue?: number;
    hours: number;
    total_hours: number;
    entries: number;
    avg_rate?: number;
    days_worked: number;
    revenue_share?: number;
  }>;
  top_clients: Array<{
    rank: number;
    id: number;
    name: string;
    revenue?: number;
    hours: number;
    entries: number;
    matters: number;
    revenue_share?: number;
  }>;
  invoicing: {
    total_invoiced?: number;
    invoice_count: number;
    paid: { count: number; amount?: number };
    pending: { count: number; amount?: number };
    overdue: { count: number; amount?: number };
    avg_payment_days: number;
    total_outstanding?: number;
    total_overdue?: number;
  };
  my_stats: {
    user_info: { id: number; name: string; email: string; firm: string };
    period: { type: string; start_date: string; end_date: string };
    summary: Record<string, any>;
    targets?: Record<string, any>;
    top_work?: {
      clients: Array<Record<string, any>>;
      matters: Array<Record<string, any>>;
    };
  } | null;
  generated_at: string;
}

export type PeriodType = 'month' | 'quarter' | 'year' | 'last_month' | 'last_quarter' | 'last_year' | 'custom';

export interface StatisticsOutletContext {
  period: PeriodType;
  loading: boolean;
  error: string | null;
  data: BusinessDashboard | null;
  hasFinancialData: boolean;
  labels: { current: string; previous: string };
  fetchData: () => void;
  preferTableView: boolean;
  customRange?: { start: string; end: string };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const formatCurrency = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return '€0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '€0';
  return new Intl.NumberFormat('en-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

export const formatCurrencyCompact = (value: number): string => {
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}K`;
  return `€${value.toFixed(0)}`;
};

export const formatHours = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return '0h';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0h';
  return `${num.toFixed(0)}h`;
};

export const PERIOD_LABELS: Record<PeriodType, { current: string; previous: string }> = {
  month: { current: 'This Month', previous: 'Last Month' },
  quarter: { current: 'This Quarter', previous: 'Last Quarter' },
  year: { current: 'This Year', previous: 'Last Year' },
  last_month: { current: 'Last Month', previous: 'Month Before' },
  last_quarter: { current: 'Last Quarter', previous: 'Quarter Before' },
  last_year: { current: 'Last Year', previous: 'Year Before' },
  custom: { current: 'Custom Range', previous: 'Previous Period' },
};

export const COMPARE_COLORS = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
];

// ─── Shared Sub-components ───────────────────────────────────────────────────

export function KPICard({ title, value, subtitle, change, changeLabel, loading }: {
  title: string;
  value: string;
  subtitle?: string;
  change?: number;
  changeLabel?: string;
  loading: boolean;
}) {
  const { hideAmounts, amountClassName } = useHideAmounts();
  const isCurrency = value.includes('€');
  const displayValue = isCurrency && hideAmounts ? '•••' : value;
  const displaySubtitle = subtitle && subtitle.includes('€') && hideAmounts ? '•••' : subtitle;
  return (
    <Card>
      <CardContent className="pt-6 pb-4">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-3 w-28" />
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className={cn('text-2xl font-bold mt-1 tracking-tight', isCurrency && amountClassName)}>{displayValue}</p>
            {change !== undefined && change !== null ? (
              <div className={cn(
                'text-sm mt-1 flex items-center gap-1',
                change > 0 ? 'text-emerald-600' : change < 0 ? 'text-rose-600' : 'text-muted-foreground'
              )}>
                {change > 0 ? <TrendingUp className="h-3.5 w-3.5" /> :
                  change < 0 ? <TrendingDown className="h-3.5 w-3.5" /> :
                  <Minus className="h-3.5 w-3.5" />}
                <span>{change > 0 ? '+' : ''}{change.toFixed(1)}%</span>
                {changeLabel && <span className="text-muted-foreground">{changeLabel}</span>}
              </div>
            ) : displaySubtitle ? (
              <p className={cn('text-sm text-muted-foreground mt-1', displaySubtitle.includes('€') && amountClassName)}>{displaySubtitle}</p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function ComparisonRow({ label, current, previous, change, format }: {
  label: string;
  current: number;
  previous: number;
  change: number;
  format: 'currency' | 'hours' | 'number';
}) {
  const { hideAmounts, amountClassName } = useHideAmounts();
  const isCurr = format === 'currency';
  const fmt = (v: number) => {
    if (isCurr) return hideAmounts ? '•••' : formatCurrency(v);
    if (format === 'hours') return formatHours(v);
    return v.toLocaleString();
  };

  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <span className="text-sm font-medium w-28">{label}</span>
      <div className="flex items-center gap-3">
        <span className={cn('text-sm text-muted-foreground w-24 text-right', isCurr && amountClassName)}>{fmt(previous)}</span>
        <span className="text-muted-foreground">→</span>
        <span className={cn('text-sm font-semibold w-24 text-right', isCurr && amountClassName)}>{fmt(current)}</span>
        <span className={cn(
          'text-sm w-16 text-right font-medium',
          change > 0 ? 'text-emerald-600' : change < 0 ? 'text-rose-600' : 'text-muted-foreground'
        )}>
          {change > 0 ? '+' : ''}{change.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

export function ChartTooltip({ active, payload, label, format = 'currency' }: any) {
  const { hideAmounts } = useHideAmounts();
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover text-popover-foreground border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-muted-foreground">
          {entry.name}: {format === 'currency' ? (hideAmounts ? '•••' : formatCurrency(entry.value)) :
                         format === 'hours' ? formatHours(entry.value) : entry.value}
        </p>
      ))}
    </div>
  );
}

export function LoadingGrid({ count = 4 }: { count?: number }) {
  return (
    <div className={`grid grid-cols-2 lg:grid-cols-${count} gap-4`}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="pt-6 pb-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-3 w-28" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
